<template>
  <ul v-if="computedLinks.length > 0" class="side__menu__footer">
    <li v-for="link in computedLinks" :key="link.path">
      <nuxt-link
        :to="link.path"
        :class="{ 'footer-link--highlight': link.highlight }"
        :data-testid="`footer-link-${toTestIdToken(link.label)}`"
        :aria-label="link.label"
        @click="handleClick(link)"
      >
        <span :class="{ hidden: !hovered }">{{ link.label }}</span>
      </nuxt-link>
    </li>
  </ul>
</template>

<script>
import { hasSubscriptionAccess } from '~~/shared/billing/subscription-status'
import { useBillingStatus } from '~/composables/useBillingStatus'

export default {
  props: {
    hovered: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const config = useRuntimeConfig()
    return { publicConfig: config.public, ...useBillingStatus() }
  },
  data() {
    return {
      links_laniqo: [
        {
          path: 'https://laniqo.com/privacy-policy/',
          label: this.$t('footer.privacy_policy'),
        },
        { path: 'mailto:info@laniqo.com', label: this.$t('footer.contact') },
      ],
      links_kern: [],
    }
  },
  computed: {
    showRevokeLink() {
      return hasSubscriptionAccess(this.billingStatus)
    },
    computedLinks() {
      const instanceVendor = this.publicConfig.instanceVendor || 'laniqo'
      if (instanceVendor === 'kern') {
        return this.links_kern
      } else if (instanceVendor === 'laniqo') {
        return this.links_laniqo
      } else if (this.isPonsVendor()) {
        return this.getPonsLinks()
      }
      return []
    },
  },
  async mounted() {
    await this.ensureBillingStatusLoaded()
  },
  methods: {
    handleClick(link) {
      if (link.action) {
        link.action()
      }
    },
    async openCookiePolicy() {
      await this.showCookieConsentForm()
    },

    async showCookieConsentForm(retryCount = 0, maxRetries = 10) {
      const checkOneTrustAvailable = () => {
        return (
          window.Optanon &&
          typeof window.Optanon.ToggleInfoDisplay === 'function'
        )
      }

      if (checkOneTrustAvailable()) {
        try {
          window.Optanon.ToggleInfoDisplay()
        } catch (error) {
          console.error('Error opening OneTrust cookie preferences:', error)
        }
        return
      }

      if (retryCount < maxRetries) {
        const delay = Math.min(200 * Math.pow(1.5, retryCount), 2000)
        await new Promise((resolve) => setTimeout(resolve, delay))
        return this.showCookieConsentForm(retryCount + 1, maxRetries)
      }

      console.warn(
        'OneTrust cookie preferences are not available. The OneTrust script may not have loaded properly.'
      )

      if (import.meta.client && this.$nuxt) {
        this.$nuxt.$emit('notification', {
          type: 'warning',
          message:
            'Cookie preferences are currently unavailable. Please try again in a moment.',
        })
      }
    },
    getPonsLinks() {
      const baseLinks = [
        {
          path: this.getPonsDataProtectionLink(),
          label: this.$t('footer.data_protection'),
        },
        {
          path: this.getPonsLegalNoticeLink(),
          label: this.$t('footer.legal_notice'),
        },
        {
          path: this.getPonsTermsAndConditionsLink(),
          label: this.$t('footer.terms_and_conditions'),
        },
        {
          path: this.getPonsTermsAndConditionsLink(),
          label:
            this.$i18n.locale === 'de'
              ? 'Copyright PONS Intelligence GmbH. Berlin, 2026. Alle Rechte vorbehalten.'
              : 'Copyright PONS Intelligence GmbH. Berlin, 2026. All rights reserved.',
        },
      ]

      if (this.isOneTrustConfigured()) {
        const cookiePolicyLink = {
          path: 'javascript:void(0)',
          label: this.$t('footer.cookie_policy'),
          action: this.openCookiePolicy,
        }

        baseLinks.splice(-1, 0, cookiePolicyLink)
      }

      if (this.showRevokeLink) {
        baseLinks.unshift({
          path: 'https://pons-intelligence.com/revoke',
          label: this.$t('footer.revoke_contract'),
          highlight: true,
        })
      }

      return baseLinks
    },
    getPonsDataProtectionLink() {
      return this.$i18n.locale === 'de'
        ? 'https://pons-intelligence.com/de-datenschutz'
        : 'https://pons-intelligence.com/en-privacy-policy'
    },
    getPonsLegalNoticeLink() {
      return this.$i18n.locale === 'de'
        ? 'https://pons-intelligence.com/de-impressum'
        : 'https://pons-intelligence.com/en-legal-notice'
    },
    getPonsTermsAndConditionsLink() {
      return this.$i18n.locale === 'de'
        ? 'https://pons-intelligence.com/de-allgemeine-geschaeftsbedingungen'
        : 'https://pons-intelligence.com/en-general-terms'
    },

    isPonsVendor() {
      return ['pons', 'pons-i'].includes(this.publicConfig.instanceVendor)
    },
    isOneTrustConfigured() {
      return this.isPonsVendor() && this.publicConfig.cookiesDataDomain
    },
    toTestIdToken(value) {
      // The previous replace collapses runs into single dashes, so
      // trimming one leading/trailing dash is enough (and linear-time).
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40)
    },
  },
}
</script>

<style lang="scss" scoped>
.side__menu__footer {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  align-items: center;
  justify-content: center;

  li {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    & a {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      width: 100%;
      text-decoration: underline;
      font-size: 0.8rem;
      background: var(--color-neutral-50);
      border-radius: 0.375rem;

      color: var(--color-neutral-600);
      transition: background-color 0.3s ease;
      text-wrap: wrap;
      min-width: 19rem;

      &:hover {
        background: var(--color-neutral-100);
        transition: 0.5s;
      }

      &:focus-visible {
        outline: none;
        box-shadow: inset 0 0 0 2px var(--color-primary-600);
      }
    }

    & a.footer-link--highlight {
      color: var(--color-primary-600);
      text-decoration: underline;
    }
  }
}

@media (max-width: 1040px) {
  .side__menu__footer {
    margin-top: 2rem;
    padding-top: 2rem;
    padding-bottom: 5rem;
    background-color: var(--color-primary-600);

    li a {
      background-color: var(--color-primary-600);
      color: var(--color-neutral-50);
      text-decoration: none;
      font-size: 1rem;
      text-wrap: wrap;

      &:hover {
        background: var(--color-primary-400);
        transition: 0.5s;
      }
    }

    li a.footer-link--highlight {
      color: var(--color-neutral-50);
      text-decoration: underline;
    }
  }
}
</style>
